/**
 * Small pieces shared by Login and ForgotPassword: the inline alert that
 * renders a mapped Cognito error (with its follow-up action), and a
 * password input with a show/hide toggle.
 */
import { useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button, Icon, TextInput } from '../components/ui';
import type { AuthErrorInfo } from '../lib/authErrors';
import './auth.css';

export type AuthAlertTone = 'danger' | 'warning' | 'info' | 'success';

export function AuthAlert({
  tone,
  title,
  detail,
  actions,
}: {
  tone: AuthAlertTone;
  title: ReactNode;
  detail?: ReactNode;
  actions?: ReactNode;
}) {
  const icon = tone === 'success' ? 'check' : tone === 'info' ? 'notify' : 'warning';
  return (
    <div className={`auth-alert auth-alert--${tone}`} role={tone === 'danger' ? 'alert' : 'status'}>
      <div className="auth-alert__title">
        <Icon name={icon} size={16} />
        <span>{title}</span>
      </div>
      {detail && <p className="auth-alert__detail">{detail}</p>}
      {actions && <div className="auth-alert__actions">{actions}</div>}
    </div>
  );
}

export interface AuthErrorAlertProps {
  info: AuthErrorInfo;
  /** Email to prefill when linking into the reset flow. */
  email?: string;
  /** Make the "Reset password" link primary (e.g. after repeated wrong passwords). */
  emphasizeReset?: boolean;
  onResendCode?: () => void;
  onRetry?: () => void;
  onSignIn?: () => void;
  /** Extra copy appended under the mapped detail (e.g. attempt counter). */
  extra?: ReactNode;
}

/** Render a mapped auth error plus the button for its suggested action. */
export function AuthErrorAlert({
  info,
  email,
  emphasizeReset,
  onResendCode,
  onRetry,
  onSignIn,
  extra,
}: AuthErrorAlertProps) {
  let actions: ReactNode = null;
  switch (info.action) {
    case 'forgotPassword':
      actions = (
        <Link
          to="/forgot-password"
          state={{ email }}
          className={`ui-btn ui-btn--sm ${emphasizeReset ? 'ui-btn--primary' : 'ui-btn--secondary'}`}
        >
          Reset my password
        </Link>
      );
      break;
    case 'resendCode':
      actions = onResendCode ? (
        <Button type="button" size="sm" variant="secondary" onClick={onResendCode}>
          Send a new code
        </Button>
      ) : null;
      break;
    case 'retry':
      actions = onRetry ? (
        <Button type="button" size="sm" variant="secondary" onClick={onRetry}>
          Try again
        </Button>
      ) : null;
      break;
    case 'signIn':
      actions = onSignIn ? (
        <Button type="button" size="sm" variant="secondary" onClick={onSignIn}>
          Back to sign in
        </Button>
      ) : null;
      break;
    default:
      actions = null;
  }
  const tone: AuthAlertTone = info.code === 'NetworkError' ? 'warning' : 'danger';
  return (
    <AuthAlert
      tone={tone}
      title={info.title}
      detail={
        <>
          {info.detail}
          {extra && (
            <>
              {' '}
              {extra}
            </>
          )}
        </>
      }
      actions={actions}
    />
  );
}

/** Password input with an in-field show/hide toggle. */
export function PasswordInput(props: InputHTMLAttributes<HTMLInputElement>) {
  const [shown, setShown] = useState(false);
  return (
    <div className="auth-pw">
      <TextInput {...props} type={shown ? 'text' : 'password'} />
      <Button
        type="button"
        size="sm"
        variant="subtle"
        className="auth-pw__toggle"
        onClick={() => setShown((s) => !s)}
        aria-label={shown ? 'Hide password' : 'Show password'}
        tabIndex={-1}
      >
        {shown ? 'Hide' : 'Show'}
      </Button>
    </div>
  );
}
